import React from "react";
import { useTranslation } from "react-i18next";
import CheckoutActionButton from "../ui/CheckoutActionButton.tsx";
import type { useSplitPayment } from "../hooks/useSplitPayment";
import { showPrice, pluralText } from "../../../../../../../utils/commonfunction.js";
import { TABLE_SLIP_PRINT_MODES } from "../../../../../../../utils/Constants";
import { ORDER_TYPE } from "../utils/constants.ts";
import { getTaxBracketBreakdown } from "../../../../../../../utils/taxBreakdown.ts";
import type {
  CashierHomeLayoutOrderSlice,
  CashierHomeLayoutUiSlice,
} from "../hooks/types.ts";

interface SummaryCardView {
  variant: "total" | "afterDiscount" | "split";
  title: string;
  finalAmount: number;
  originalAmount: number;
  savingsAmount: number;
  couponCode?: string;
  couponAmount: number;
  giftCardAmount: number;
}

interface DiscountSummaryCardProps {
  summary: SummaryCardView;
}

const DiscountSummaryCard: React.FC<DiscountSummaryCardProps> = ({ summary }) => {
  const { t } = useTranslation();
  const {
    title,
    variant,
    finalAmount,
    originalAmount,
    savingsAmount,
    couponCode,
    couponAmount,
    giftCardAmount,
  } = summary;

  if (variant === "total") {
    return (
      <div className="checkout-total-row checkout-total-summary-row">
        <span>{title}</span>
        <span className="checkout-total-final-value">
          ¥ {showPrice(savingsAmount > 0 ? originalAmount : finalAmount)}
        </span>
      </div>
    );
  }

  if (savingsAmount > 0) {
    return (
      <div className="split-total-card">
        <div className="split-total-card-header">
          <span>{title}</span>
          <span className="split-savings-pill">
            {t("label_you_save_amount", {
              amount: showPrice(savingsAmount),
            })}
          </span>
        </div>

        <div className="split-total-card-amounts">
          <span className="split-total-original">
            ¥ {showPrice(originalAmount)}
          </span>
          <span className="split-total-final">
            ¥ {showPrice(finalAmount)}
          </span>
        </div>

        <div className="split-total-card-divider"></div>

        {couponAmount > 0 ? (
          <div className="checkout-mini-row split-discount-line text-success">
            <span>
              <i className="ti ti-ticket"></i>
              {couponCode || t("coupon")}
            </span>
            <span>-¥ {showPrice(couponAmount)}</span>
          </div>
        ) : null}

        {giftCardAmount > 0 ? (
          <div className="checkout-mini-row split-discount-line text-success">
            <span>
              <i className="ti ti-gift"></i>
              {t("label_gift_card")}
            </span>
            <span>-¥ {showPrice(giftCardAmount)}</span>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="split-total-plain">
      <span className="split-total-plain-label">{title}</span>
      <span className="split-total-plain-amount">¥ {showPrice(finalAmount)}</span>
    </div>
  );
};

interface OrderSummaryProps {
  order: CashierHomeLayoutOrderSlice;
  ui: CashierHomeLayoutUiSlice;
  splitPayment: ReturnType<typeof useSplitPayment>;
  onSendToKitchen: () => void;
  onPrintTableSlip?: () => void;
  isPrintingTableSlip?: boolean;
  onPay?: () => void;
  onAddCustomItem?: () => void;
  canAddCustomItem?: boolean;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  order,
  ui,
  splitPayment,
  onSendToKitchen,
  onPrintTableSlip,
  isPrintingTableSlip = false,
  onPay,
  onAddCustomItem,
  canAddCustomItem,
}) => {
  const { t } = useTranslation();
  const {
    orderPriceDetail,
    totalOrderQuantity,
    tableDetail,
    orderItems,
    restSettingDetails,
  } = order;
  // Send to kitchen stays active while any line has un-sent units
  // (quantity beyond what was already sent to the kitchen).
  const hasUnsentItems = orderItems.some(
    (item) => Number(item.quantity || 0) - Number(item.kitchen_printed_qty || 0) > 0,
  );
  // snapshotted onto the order at creation, not read from restaurant settings
  const serviceChargePercentage = Number(tableDetail?.activeOrder?.service_charge_percentage || 0);

  const {
    isSplitCheckout,
    isSplitPayDisabled,
    currentSplitTotal,
    getRemainingAmount,
    handleProceedToSplitPay: onSplitPay,
  } = splitPayment;
  const { splitPreviewDetail } = splitPayment.splitDiscounts;

  const isOrderValid = totalOrderQuantity > 0;
  const showPrintTableSlip = order.orderType !== ORDER_TYPE.EDIT_SALES
    && restSettingDetails?.table_slip_print_mode === TABLE_SLIP_PRINT_MODES.CONSOLIDATED_AT_END;
  const remainingAmount = getRemainingAmount();

  // During a split checkout the breakdown describes the split being paid, not
  // the whole order. splitPreviewDetail is null until the backend has returned
  // a preview for the current items/amount selection.
  const breakdownDetail = isSplitCheckout && splitPreviewDetail
    ? splitPreviewDetail
    : orderPriceDetail;
  // Already net of any discount, which fills the 10% dine-in bracket before it
  // touches the 8% take-out one.
  const taxBreakdown = getTaxBracketBreakdown(breakdownDetail ?? undefined);
  // The totals are the sum of the bracket rows above them rather than the
  // order's gross subtotal/tax_price, so the block shows what is actually
  // charged after a discount — and adds up on screen.
  const netSubtotal = taxBreakdown.dineInSubtotal + taxBreakdown.takeOutSubtotal;
  const netConsumptionTax = taxBreakdown.dineInTax + taxBreakdown.takeOutTax;
  // split-by-amount has no item basis, so it shows no count
  const breakdownQuantity = isSplitCheckout && splitPreviewDetail
    ? (splitPayment.splitItemsGroup?.splitItems ?? []).reduce(
      (sum, item) => sum + Number(item?.split_quantity || 0),
      0
    )
    : totalOrderQuantity;

  // Local Summary Computations
  const originalSplitTotal =
    Number(splitPreviewDetail?.amount_after_discount || 0) +
    Number(splitPreviewDetail?.coupon_code_amount || 0) +
    Number(splitPreviewDetail?.gift_card_amount || 0);

  const splitSavings =
    Number(splitPreviewDetail?.coupon_code_amount || 0) +
    Number(splitPreviewDetail?.gift_card_amount || 0);

  const totalSavings =
    Number(orderPriceDetail?.coupon_code_amount || 0) +
    Number(orderPriceDetail?.gift_card_amount || 0);

  const totalOriginalAmount = Number(orderPriceDetail?.great_grand_total || 0);
  const totalFinalAmount = Number(
    orderPriceDetail?.amount_after_discount || orderPriceDetail?.great_grand_total || 0
  );

  const sharedDiscountData = {
    finalAmount: totalFinalAmount,
    originalAmount: totalOriginalAmount,
    savingsAmount: totalSavings,
    couponCode: orderPriceDetail?.coupon_code,
    couponAmount: Number(orderPriceDetail?.coupon_code_amount || 0),
    giftCardAmount: Number(orderPriceDetail?.gift_card_amount || 0),
  };

  const totalSummary: SummaryCardView = {
    variant: "total",
    title: t("label_total"),
    ...sharedDiscountData,
  };

  const afterDiscountSummary: SummaryCardView = {
    variant: "afterDiscount",
    title: t("label_after_discount"),
    ...sharedDiscountData,
  };

  const splitSummary: SummaryCardView = {
    variant: "split",
    title: t("label_your_split"),
    finalAmount: Number(currentSplitTotal || 0),
    originalAmount: Number(originalSplitTotal || currentSplitTotal || 0),
    savingsAmount: Number(splitSavings || 0),
    couponCode: splitPreviewDetail?.coupon_code,
    couponAmount: Number(splitPreviewDetail?.coupon_code_amount || 0),
    giftCardAmount: Number(splitPreviewDetail?.gift_card_amount || 0),
  };

  return (
    <div className="checkout-summary-footer">
      {onAddCustomItem ? (
        <div className="checkout-custom-item-summary-toolbar">
          <button
            type="button"
            className="btn btn-border checkout-custom-item-trigger"
            onClick={onAddCustomItem}
            disabled={canAddCustomItem === false}
          >
            {t("label_add_custom_item")}
          </button>
        </div>
      ) : null}
      <div className="subtotal-info p-3">
        <ul className="d-flex flex-column gap-3">
          <li className="subtotal-line-muted">
            <h4>{t("label_dine_in_subtotal")}</h4>
            <h4>¥ {showPrice(taxBreakdown.dineInSubtotal)}</h4>
          </li>
          <li className="subtotal-line-muted">
            <h4>{t("label_take_out_subtotal")}</h4>
            <h4>¥ {showPrice(taxBreakdown.takeOutSubtotal)}</h4>
          </li>
          <li className="subtotal-line-strong">
            <h4>
              {t("sub_total_label")}
              {breakdownQuantity > 0
                ? ` (${breakdownQuantity} ${pluralText(
                  breakdownQuantity,
                  t("label_item"),
                  t("label_items")
                )})`
                : ""}
            </h4>
            <h4>¥ {showPrice(netSubtotal)}</h4>
          </li>
        </ul>

        {breakdownDetail?.service_charge ? (
          <ul className="d-flex flex-column gap-3">
            <li>
              <h4>
                {t("service_charge")}
                {serviceChargePercentage > 0 ? ` (${serviceChargePercentage}%)` : ""}
              </h4>
              <h4>¥ {showPrice(Number(breakdownDetail?.service_charge || 0))}</h4>
            </li>
          </ul>
        ) : null}

        <ul className="d-flex flex-column gap-3">
          <li className="subtotal-line-muted">
            <h4>10% {t("label_dine_in_tax")}</h4>
            <h4>¥ {showPrice(taxBreakdown.dineInTax)}</h4>
          </li>
          <li className="subtotal-line-muted">
            <h4>8% {t("label_take_out_tax")}</h4>
            <h4>¥ {showPrice(taxBreakdown.takeOutTax)}</h4>
          </li>
          <li className="subtotal-line-strong">
            <h4>{t("label_consumption_tax")}</h4>
            <h4>¥ {showPrice(netConsumptionTax)}</h4>
          </li>
        </ul>

        {breakdownDetail?.total_tax_8 &&
        breakdownDetail?.total_tax_8 > 0 &&
        restSettingDetails?.icon_display_eligible_for_reduced_tax_rate ? (
          <p className="d-block no-wrap">
            * {t("tax_on_other_text")} <br />
            {t("tax_rate_8")}
          </p>
        ) : null}
      </div>

      <div className="checkout-total-panel">
        <DiscountSummaryCard summary={totalSummary} />

        {isSplitCheckout && typeof remainingAmount === "number" ? (
          <div className="split-remaining-total-card">
            <span>{t("label_remaining_amount")}</span>
            <p>¥ {showPrice(remainingAmount)}</p>
          </div>
        ) : null}

        {!isSplitCheckout && totalSavings > 0 && (
          <DiscountSummaryCard summary={afterDiscountSummary} />
        )}

        {isSplitCheckout && <DiscountSummaryCard summary={splitSummary} />}

        <div className="checkout-action-row">
          {ui.activeSidebarTab === "items" ? (
            order.orderType === ORDER_TYPE.EDIT_SALES ? null : (
              <>
                <CheckoutActionButton
                  fullWidth
                  disabled={!isOrderValid || !hasUnsentItems}
                  type="button"
                  className="mb-2"
                  onClick={onSendToKitchen}
                >
                  {t("label_send_to_kitchen")}
                </CheckoutActionButton>
                {showPrintTableSlip ? (
                  <CheckoutActionButton
                    fullWidth
                    disabled={!isOrderValid || isPrintingTableSlip}
                    type="button"
                    className="mb-2"
                    onClick={onPrintTableSlip}
                  >
                    {t("label_print_table_slip")}
                  </CheckoutActionButton>
                ) : null}
                <CheckoutActionButton
                  fullWidth
                  disabled={!isOrderValid}
                  type="button"
                  onClick={() => ui.setActiveSidebarTab("payment")}
                >
                  {t("label_continue_to_payment")}
                </CheckoutActionButton>
              </>
            )
          ) : isSplitCheckout ? (
            <CheckoutActionButton
              fullWidth
              disabled={isSplitPayDisabled}
              type="button"
              onClick={onSplitPay}
            >
              {t("pay")}
            </CheckoutActionButton>
          ) : (
            <CheckoutActionButton fullWidth disabled={!isOrderValid} type="button" onClick={onPay}>
              {t("pay")}
            </CheckoutActionButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
